/** Pricing page — three zones as required by the Founding Programme brief:
 *    FREE NOW  — Founding Station (measurement, Phase 1)
 *    PAID NOW  — WaveScore Streams capacity upgrade (GH₵200/mo via Paystack)
 *    PHASE 2   — Tier table published, priced, NOT purchasable now
 *
 *  All prices, features, and CTAs come from GET /api/billing/tiers. Never
 *  hard-code numbers here — the tier table is the source of truth so a
 *  config change flows through automatically.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { T } from "@/lib/theme";
import { PHASE2_VOCAB } from "@/lib/labels";
import { trackClick } from "@/lib/telemetry";

const C = T.dark;
const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const ZONE_META = {
  free_now:  { title: "Free now",   color: T.greenAccent, subtitle: "Phase 1 — while we build the standard" },
  paid_now:  { title: "Paid now",   color: "#60A5FA",     subtitle: "Live paid product on Paystack" },
  phase2:    { title: "Phase 2",    color: "#F59E0B",     subtitle: "Priced, coming with the representative layer" },
};


export default function Pricing() {
  const nav = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [streamsUpgradeEnabled, setStreamsUpgradeEnabled] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/billing/tiers`).then(({ data }) => {
      setTiers(data.tiers || []);
      setStreamsUpgradeEnabled(!!data.streams_upgrade_enabled);
    }).catch(() => {});
  }, []);

  const byZone = (z) => tiers.filter((t) => t.zone === z);

  return (
    <div data-testid="pricing-page" style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Inter',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@700;800&display=swap'); body{margin:0;background:${C.bg}}`}</style>

      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 28px",
        display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ textDecoration: "none", color: C.text, fontWeight: 800, fontSize: 18 }}>
          WaveScore<span style={{ color: T.greenAccent }}>Pro</span>
        </Link>
        <button data-testid="pricing-back" onClick={() => nav(-1)}
          style={{ background: "transparent", color: C.text, border: `1px solid ${C.border}`,
            padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
          ← Back
        </button>
      </div>

      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "48px 28px 80px" }}>
        <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 }}>
          Pricing · Phase 1
        </div>
        <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 38, fontWeight: 800, margin: "0 0 14px", letterSpacing: "-0.02em" }}>
          Free measurement. One live paid product. Phase 2 priced honestly.
        </h1>

        {/* Phase-1 scope banner — from the labels module */}
        <div data-testid="pricing-scope-banner" style={{
          marginBottom: 36, padding: 18, borderRadius: 12,
          background: "rgba(245, 158, 11, 0.08)",
          border: "1px solid rgba(245, 158, 11, 0.32)",
          color: "#FBBF24", fontSize: 13, lineHeight: 1.6,
        }}>
          <strong style={{ display: "block", marginBottom: 4, fontSize: 14, color: "#FCD34D" }}>
            Phase 1 scope
          </strong>
          {PHASE2_VOCAB.phase2_pricing_banner}
        </div>

        {/* ── FREE NOW ─────────────────────────────────────────────── */}
        {byZone("free_now").length > 0 && (
          <Zone id="free_now">
            {byZone("free_now").map((t) => <TierCard key={t.id} tier={t} enabled />)}
          </Zone>
        )}

        {/* ── PAID NOW ─────────────────────────────────────────────── */}
        {byZone("paid_now").length > 0 && (
          <Zone id="paid_now">
            {byZone("paid_now").map((t) => (
              <TierCard key={t.id} tier={t} enabled={streamsUpgradeEnabled}
                disabledNote={!streamsUpgradeEnabled && "Awaiting Paystack activation — capacity upgrade launches shortly."} />
            ))}
          </Zone>
        )}

        {/* ── PHASE 2 ──────────────────────────────────────────────── */}
        {byZone("phase2").length > 0 && (
          <>
            <Zone id="phase2">
              {byZone("phase2").map((t) => <TierCard key={t.id} tier={t} enabled={false} />)}
            </Zone>
            <div data-testid="pricing-founding-note" style={{
              marginTop: 20, padding: 16, borderRadius: 10,
              background: "rgba(0,200,150,0.08)", border: "1px solid rgba(0,200,150,0.32)",
              color: T.greenAccent, fontSize: 13, textAlign: "center",
            }}>
              Founding stations save <strong>50%</strong> on these Phase-2 rates —
              <Link to="/streams" style={{ color: T.greenAccent, marginLeft: 6 }}>claim your station</Link> to lock in your Founding rate.
            </div>
          </>
        )}
      </div>

      <footer style={{ borderTop: `1px solid ${C.border}`, padding: "24px 28px", textAlign: "center", color: C.muted, fontSize: 12 }}>
        © {new Date().getFullYear()} I-Assist Pro Limited · Accra, Ghana
      </footer>
    </div>
  );
}


function Zone({ id, children }) {
  const meta = ZONE_META[id];
  return (
    <section data-testid={`pricing-zone-${id}`} style={{ marginBottom: 44 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 18, flexWrap: "wrap" }}>
        <div style={{
          padding: "4px 12px", borderRadius: 999,
          background: `${meta.color}22`, border: `1px solid ${meta.color}44`,
          color: meta.color, fontSize: 11, fontWeight: 700,
          letterSpacing: "0.14em", textTransform: "uppercase",
        }}>{meta.title}</div>
        <span style={{ color: C.muted, fontSize: 13 }}>{meta.subtitle}</span>
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: 18,
      }}>
        {children}
      </div>
    </section>
  );
}


function TierCard({ tier, enabled, disabledNote }) {
  const priceLabel = (() => {
    if (tier.zone === "free_now") return "GH₵0";
    if (tier.zone === "paid_now") return `GH₵${tier.price_now_ghs?.toLocaleString() ?? "—"}/mo`;
    if (tier.zone === "phase2")   return `GH₵${tier.price_future_ghs?.toLocaleString() ?? "—"}/mo`;
    return "—";
  })();

  const ctaEnabled = enabled && (tier.zone !== "phase2");
  const ctaHref = ctaEnabled && tier.cta_link;

  return (
    <div data-testid={`pricing-tier-${tier.id}`} style={{
      padding: 22, borderRadius: 14,
      background: C.surface, border: `1px solid ${C.border}`,
      display: "flex", flexDirection: "column",
      opacity: (tier.zone === "phase2") ? 0.85 : 1,
    }}>
      <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 6 }}>{tier.name}</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.55 }}>{tier.tagline}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: C.text, marginBottom: 4, fontFamily: "'IBM Plex Mono',monospace" }}>
        {priceLabel}
      </div>
      {tier.zone === "phase2" && (
        <div style={{ fontSize: 11, color: "#F59E0B", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>
          Phase 2 · not yet purchasable
        </div>
      )}
      <ul style={{ margin: "8px 0 20px", padding: 0, listStyle: "none", flex: 1 }}>
        {(tier.features || []).map((f, i) => {
          const isPhase2 = /^Phase 2/i.test(f);
          return (
            <li key={i} style={{ padding: "6px 0", fontSize: 13, color: isPhase2 ? "#FBBF24" : C.text, lineHeight: 1.5 }}>
              {isPhase2 ? "◇" : "✓"} {f}
            </li>
          );
        })}
      </ul>
      {ctaHref ? (
        <Link to={ctaHref} data-testid={`pricing-cta-${tier.id}`}
          onClick={() => trackClick(`pricing.tier.${tier.id}`)}
          style={{
          padding: "10px 16px", borderRadius: 999,
          background: T.greenAccent, color: "#0A1628",
          textDecoration: "none", fontSize: 13, fontWeight: 700, textAlign: "center",
        }}>{tier.cta}</Link>
      ) : (
        <div data-testid={`pricing-cta-${tier.id}`} style={{
          padding: "10px 16px", borderRadius: 999,
          background: "rgba(255,255,255,0.05)",
          color: C.muted, fontSize: 12, fontWeight: 600,
          textAlign: "center", border: `1px solid ${C.border}`,
        }}>{tier.cta}</div>
      )}
      {disabledNote && (
        <div style={{ marginTop: 10, fontSize: 11, color: C.muted, textAlign: "center", fontStyle: "italic" }}>
          {disabledNote}
        </div>
      )}
    </div>
  );
}
